// What the draw tools draw with: per-tool appearance (fill / stroke /
// thickness, canopy for ellipses) plus a layer and dates shared by every
// tool. Picked in Properties while a draw tool is active (v1's draw menu) and
// applied to each new shape. Placing a plant works the same way, with its own
// appearance under `placement` — kept for one species at a time (see
// placementStyles). Session state only — never saved with the plan.

import { DEFAULT_FILL_COLOR, DEFAULT_STROKE_COLOR } from '../migrations';

export const DRAW_TOOLS = ['rectangle', 'ellipse', 'polygon', 'line'];

export function isDrawTool(tool) { return DRAW_TOOLS.includes(tool); }

// Tools whose next element is set up in Properties: the draw tools + placement.
export function hasDrawOptions(tool) { return isDrawTool(tool) || tool === 'placement'; }

// Beds and canopies start green with a hairline; lines (fences, paths) start
// as a visible 6 cm stroke with no fill. Polygons and lines also carry the
// segment type each new segment is drawn with (v1's draw menu).
export function defaultDrawStyles() {
  const shape = () => ({ fillColor: DEFAULT_FILL_COLOR, strokeColor: DEFAULT_STROKE_COLOR, strokeWidth: null });
  return {
    rectangle: shape(),
    ellipse: { ...shape(), canopyStyle: 'smooth' },
    polygon: { ...shape(), segmentType: 'straight' },
    line: { fillColor: null, strokeColor: DEFAULT_STROKE_COLOR, strokeWidth: 6, segmentType: 'straight' },
    // Placed plants: v1's plant look (makePlantElement) — green, no stroke, lobed.
    placement: { fillColor: DEFAULT_FILL_COLOR, strokeColor: null, strokeWidth: null, canopyStyle: 'lobed' },
    shared: { layer: null, addedDate: null, removedDate: null },
  };
}

// The data fields a new `tool` shape starts with.
export function drawStyleData(styles, tool, seed = 1) {
  const style = styles?.[tool] || defaultDrawStyles()[tool] || {};
  const shared = styles?.shared || {};
  const data = {
    fillColor: tool === 'line' ? null : (style.fillColor ?? null),
    strokeColor: style.strokeColor ?? null,
    strokeWidth: style.strokeWidth ?? null,
    layer: shared.layer ?? null,
    addedDate: shared.addedDate ?? null,
    removedDate: shared.removedDate ?? null,
  };
  if ((tool === 'ellipse' || tool === 'placement') && style.canopyStyle && style.canopyStyle !== 'smooth') {
    data.canopyStyle = style.canopyStyle;
    data.canopySeed = seed;
  }
  return data;
}

// The styles to start placing `speciesId` with: its appearance tweaks survive
// only while the same species is placed again — another plant starts from the
// default plant look. `layer` (the plant's own, when the plan has it) becomes
// the shared layer; null leaves the shared layer as it is.
export function placementStyles(styles, speciesId, layer = null) {
  let next = styles?.placement?.speciesId === speciesId
    ? styles
    : { ...styles, placement: { ...defaultDrawStyles().placement, speciesId } };
  if (layer) next = patchDrawStyle(next, 'shared', { layer });
  return next;
}

// Patch one tool's style (or the shared layer/dates with tool 'shared').
export function patchDrawStyle(styles, tool, patch) {
  return { ...styles, [tool]: { ...styles[tool], ...patch } };
}
